import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Eye, Users } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import { ReferenceWeekView } from "../components/programBuilder/ReferenceWeekView";
import { TooltipProvider } from "../components/ui/tooltip";
import { ProgressionMatrix } from "../components/programBuilder/ProgressionMatrix";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchAPI } from "../lib/utils";

/**
 * Sola lettura di un programma condiviso.
 *
 * Un programma condiviso — direttamente o attraverso un coach team — si legge e
 * si duplica, non si scrive (docs/coach-teams/DECISIONS.md §2). Questa pagina e'
 * quello che vede un co-coach **al posto** del ProgramBuilder.
 *
 * Non e' un secondo editor messo in grigio: riusa la resa read-only che esiste
 * gia' nel repo (`ReferenceWeekView`, nata per affiancare un programma di
 * riferimento all'editor, e `ProgressionMatrix`). Rendere di sola lettura le
 * ~17.000 righe del builder sarebbe stata la voce di costo piu' alta del piano,
 * e avrebbe lasciato in giro campi ancora focalizzabili su cui il coach poteva
 * scrivere per poi perdere tutto al salvataggio.
 */
export default function ProgramView() {
	const { programId } = useParams();
	const navigate = useNavigate();
	const { user } = useAuth();
	const { t } = useLanguage();

	const [program, setProgram] = useState(null);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);
	const [weekIndex, setWeekIndex] = useState(0);
	const [mode, setMode] = useState("week"); // week | matrix
	const [duplicating, setDuplicating] = useState(false);

	const isAdmin = user?.is_admin || user?.role === "admin";
	const isCoach = user?.role === "coach" || isAdmin;

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const data = await fetchAPI(`/programs/${programId}`);
				if (!cancelled) setProgram(data);
			} catch {
				if (!cancelled) setFailed(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [programId]);

	const weeks = useMemo(() => program?.weeks || [], [program]);
	const week = weeks[weekIndex] || null;

	// Da quale team arriva: il payload porta gia' `assigned_coach_teams`, ed e'
	// la differenza fra "condiviso" e "condiviso dal team Palestra Nord" — cioe'
	// fra un badge generico e la risposta a "perche' sto vedendo questa roba".
	const sharedTeamNames = useMemo(() => {
		const mine = new Set(
			(user?.coached_teams || []).map((team) => team.team_id).filter(Boolean),
		);
		return (program?.assigned_coach_teams || [])
			.filter((team) => mine.has(team.team_id))
			.map((team) => team.name)
			.filter(Boolean);
	}, [program, user]);

	const handleDuplicate = useCallback(async () => {
		if (duplicating) return;
		setDuplicating(true);
		try {
			const copy = await fetchAPI(`/programs/${programId}/duplicate`, {
				method: "POST",
			});
			toast.success(t("programViewDuplicated"));
			navigate(`/programs/${copy.program_id}`);
		} catch (error) {
			toast.error(error?.message || t("saveError"));
		} finally {
			setDuplicating(false);
		}
	}, [duplicating, navigate, programId, t]);

	if (!isCoach) {
		return <Navigate to="/dashboard" replace />;
	}

	// Il proprietario non ha niente da fare qui: lo si rimanda all'editor, cosi'
	// un link alla vista di lettura non diventa un vicolo cieco per chi puo'
	// scrivere.
	if (!loading && program && program.coach_id === user?.user_id) {
		return <Navigate to={`/programs/${programId}`} replace />;
	}

	return (
		<Layout>
			<div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-4">
				<div className="flex items-center justify-between gap-3 flex-wrap">
					<Button
						variant="ghost"
						size="sm"
						className="rounded-sm -ml-2"
						onClick={() => navigate("/programs")}
					>
						<ArrowLeft className="w-4 h-4 mr-1.5" />
						{t("programViewBack")}
					</Button>

					{program && (
						<Button
							variant="outline"
							size="sm"
							className="rounded-sm"
							disabled={duplicating}
							onClick={handleDuplicate}
						>
							<Copy className="w-4 h-4 mr-1.5" />
							{t("programViewDuplicate")}
						</Button>
					)}
				</div>

				{loading ? (
					<div className="flex items-center justify-center min-h-[40vh]">
						<div className="spinner" />
					</div>
				) : failed || !program ? (
					<Card className="rounded-sm">
						<CardContent className="py-10 text-center text-sm text-muted-foreground">
							{t("programViewLoadError")}
						</CardContent>
					</Card>
				) : (
					<>
						<Card className="rounded-sm">
							<CardContent className="p-4 space-y-2">
								<div className="flex items-start justify-between gap-3 flex-wrap">
									<div className="min-w-0">
										<h1 className="text-xl font-semibold truncate">
											{program.title || program.name}
										</h1>
										{program.description && (
											<p className="text-sm text-muted-foreground mt-0.5">
												{program.description}
											</p>
										)}
									</div>
									<span className="flex items-center gap-1.5 shrink-0 rounded-sm border border-volt/50 bg-volt/10 px-2 py-1 text-[11px] text-volt">
										<Eye className="w-3.5 h-3.5" />
										{t("programViewReadonlyBadge")}
									</span>
								</div>

								<p className="text-xs text-muted-foreground">
									{t("createdByLabel")}:{" "}
									{program.shared_by?.name || program.coach_name || "-"}
								</p>

								{sharedTeamNames.length > 0 && (
									<p className="flex items-center gap-1.5 text-xs text-acid">
										<Users className="w-3.5 h-3.5" />
										{t("programViewSharedVia")}: {sharedTeamNames.join(", ")}
									</p>
								)}

								<p className="text-xs text-muted-foreground border-t border-border/60 pt-2">
									{t("programViewReadonlyNote")}
								</p>
							</CardContent>
						</Card>

						{weeks.length === 0 ? (
							<Card className="rounded-sm">
								<CardContent className="py-10 text-center text-sm text-muted-foreground">
									{t("programViewNoWeeks")}
								</CardContent>
							</Card>
						) : (
							<Card className="rounded-sm overflow-hidden">
								<div className="flex items-center gap-2 p-2 border-b border-border flex-wrap">
									<Select
										value={String(weekIndex)}
										onValueChange={(value) => setWeekIndex(Number(value))}
									>
										<SelectTrigger className="w-48 h-8 rounded-sm bg-secondary border-transparent">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{weeks.map((item, index) => (
												<SelectItem
													key={item?.week_id || index}
													value={String(index)}
												>
													{String(item?.label || "").trim() ||
														`${t("week")} ${item?.week_number || index + 1}`}
												</SelectItem>
											))}
										</SelectContent>
									</Select>

									<div className="flex items-center gap-1 ml-auto">
										<Button
											variant={mode === "week" ? "default" : "outline"}
											size="sm"
											className="rounded-sm h-8"
											onClick={() => setMode("week")}
										>
											{t("pbViewWeek")}
										</Button>
										<Button
											variant={mode === "matrix" ? "default" : "outline"}
											size="sm"
											className="rounded-sm h-8"
											onClick={() => setMode("matrix")}
										>
											{t("pbViewProgression")}
										</Button>
									</div>
								</div>

								{mode === "week" ? (
									// `ReferenceWeekView` usa i tooltip di Radix per l'icona
									// degli esercizi condivisi in sola lettura, e quelli senza
									// un provider sopra non renderizzano: sollevano. Nel builder
									// il provider lo monta il dock, qui lo montiamo noi.
									<TooltipProvider delayDuration={250}>
										<ReferenceWeekView week={week} />
									</TooltipProvider>
								) : (
									<ProgressionMatrix weeks={weeks} activeWeekIndex={weekIndex} />
								)}
							</Card>
						)}
					</>
				)}
			</div>
		</Layout>
	);
}
